import { escapeHtml } from "./text.ts";

export interface ConfirmDialogOptions {
  title: string;
  message: string;
  confirmLabel: string;
  danger?: boolean;
}

export interface TextDialogOptions {
  title: string;
  label: string;
  value?: string;
  placeholder?: string;
  confirmLabel: string;
  message?: string;
  inputType?: "text" | "email" | "password";
}

function mountDialog(dialog: HTMLDialogElement, initialFocus: string): Promise<string> {
  const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  document.body.append(dialog);
  return new Promise(resolve => {
    const finish = () => {
      const value = dialog.returnValue;
      dialog.remove();
      previous?.focus({ preventScroll: true });
      resolve(value);
    };
    dialog.addEventListener("close", finish, { once: true });
    dialog.addEventListener("click", event => { if (event.target === dialog) dialog.close("cancel"); });
    dialog.showModal();
    dialog.querySelector<HTMLElement>(initialFocus)?.focus();
  });
}

export async function confirmDialog(options: ConfirmDialogOptions): Promise<boolean> {
  const dialog = document.createElement("dialog");
  dialog.className = `name-dialog confirmation-dialog ${options.danger ? "confirmation-dialog--danger" : ""}`;
  dialog.setAttribute("aria-label", options.title);
  dialog.innerHTML = `<form method="dialog"><div class="dialog-icon" aria-hidden="true"><svg class="icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 9v4m0 4h.01M10.3 3.7 2.6 17a2 2 0 0 0 1.7 3h15.4a2 2 0 0 0 1.7-3L13.7 3.7a2 2 0 0 0-3.4 0Z"/></svg></div><h2>${escapeHtml(options.title)}</h2><p>${escapeHtml(options.message)}</p><div class="dialog-actions"><button class="button button--soft" value="cancel">Cancel</button><button class="button ${options.danger ? "button--danger" : "button--primary"}" value="confirm">${escapeHtml(options.confirmLabel)}</button></div></form>`;
  return await mountDialog(dialog, "[value=cancel]") === "confirm";
}

export async function textDialog(options: TextDialogOptions): Promise<string | null> {
  const dialog = document.createElement("dialog");
  dialog.className = "name-dialog text-dialog";
  dialog.setAttribute("aria-label", options.title);
  dialog.innerHTML = `<form method="dialog"><h2>${escapeHtml(options.title)}</h2>${options.message ? `<p>${escapeHtml(options.message)}</p>` : ""}<label class="field-label">${escapeHtml(options.label)}<input name="value" type="${options.inputType || "text"}" value="${escapeHtml(options.value || "")}" placeholder="${escapeHtml(options.placeholder || "")}" maxlength="120" autocomplete="off" required></label><div class="dialog-actions"><button class="button button--soft" value="cancel" formnovalidate>Cancel</button><button class="button button--primary" value="confirm">${escapeHtml(options.confirmLabel)}</button></div></form>`;
  const result = await mountDialog(dialog, "input");
  if (result !== "confirm") return null;
  return dialog.querySelector<HTMLInputElement>("input")?.value.trim() || null;
}
